// The signed-in account in this browser. The server is the authority; the last account seen is
// remembered only so a prepared show can reopen offline for the same person. Signing out removes it.

export interface Account {
  id: string;
  name: string;
  image: string | null;
  email: string;
  signInMethod: "google" | "magic-link";
  emailVerified: boolean;
}

export class AuthError extends Error {}
export class ConnectionError extends Error {}

const LAST_ACCOUNT = "q2q-account";

export function rememberedAccount(): Account | null {
  try {
    const a = JSON.parse(localStorage.getItem(LAST_ACCOUNT) ?? "null") as Account | null;
    return a && typeof a.id === "string" ? { ...a, image: typeof a.image === "string" ? a.image : null } : null;
  } catch {
    return null;
  }
}

export function forgetAccount(): void {
  localStorage.removeItem(LAST_ACCOUNT);
}

export function rememberAccount(account: Account): void {
  localStorage.setItem(LAST_ACCOUNT, JSON.stringify(account));
}

/** GET an API path as JSON. Network failure, sign-in and other HTTP errors stay distinguishable. */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  let r: Response;
  try {
    r = await fetch(path, { cache: "no-store", credentials: "same-origin", ...init, headers: { ...(init.body && !(init.body instanceof FormData) ? { "content-type": "application/json" } : {}), ...init.headers } });
  } catch {
    throw new ConnectionError("Can't reach q2cue");
  }
  if (r.status === 401) throw new AuthError("Your session has ended. Sign in again; your changes stay on this device.");
  const isJson = r.headers.get("content-type")?.includes("application/json");
  let body: (T & { message?: string; error?: string }) | null = null;
  if (isJson) {
    try {
      body = await r.json() as T & { message?: string; error?: string };
    } catch (error) {
      // A known HTTP refusal must never become an offline fallback if its body stalls.
      if (!r.ok) throw new ApiError(r.status, "error", `q2cue answered ${r.status}`, null);
      // A response can arrive but then stall while its body is read. That is still a
      // transport failure, not a reason to skip this account's prepared offline copy.
      if (init.signal?.aborted || error instanceof TypeError || (error instanceof DOMException && error.name === "AbortError")) {
        throw new ConnectionError("Can't reach q2cue");
      }
      throw error;
    }
  }
  if (!r.ok) throw new ApiError(r.status, body?.error ?? "error", body?.message ?? `q2cue answered ${r.status}`, body);
  if (!isJson) throw new AuthError("The host returned an unexpected page. Reconnect and sign in again; your changes stay on this device.");
  return body as T;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly body: unknown;
  constructor(status: number, code: string, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

export const post = <T>(path: string, body: unknown = {}) => api<T>(path, { method: "POST", body: JSON.stringify(body) });

/** The current account from the server; remembered for offline reopening. */
export async function fetchAccount(signal?: AbortSignal): Promise<{ user: Account; environment: "local" | "staging" | "production"; limits: { pdfBytes: number; pdfPages: number; trashDays: number } }> {
  const me = await api<{ user: Account; environment: "local" | "staging" | "production"; limits: { pdfBytes: number; pdfPages: number; trashDays: number } }>("/api/me", { signal });
  rememberAccount(me.user);
  return me;
}

export function signInUrl(): string {
  return `/signin?return=${encodeURIComponent(location.pathname + location.search)}`;
}
